import type { FieldDefinition } from '@cms/contracts'
import { useI18n } from '../i18n/context.js'
import { resolveControlType } from '../domain/field-model.js'
import { labelForKey } from '../domain/humane.js'
import { translate } from '../i18n/t.js'
import type { Locale } from '../i18n/dict.js'
import { isBag, looksLikeHtml } from '../domain/humane.js'
import { Switch, TextArea, TextField } from '../ui/fields.js'
import { ListFieldControl } from './ListFieldControl.js'
import { MediaFieldControl } from './MediaFieldControl.js'
import { RichTextEditor } from './RichTextEditor.js'
import { GroupBox, ValueEditor } from './ValueEditor.js'

interface FieldControlProps {
  field: FieldDefinition
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
  /** Replaces the declared help, e.g. to explain why a field is locked. */
  supporting?: string
}

export function fieldLabel(field: FieldDefinition, locale: string): string {
  // A field the type does not name still gets a word, in the person's language ("Title" → "Titolo"), never the raw key.
  return field.label?.[locale] ?? field.label?.it ?? labelForKey((key) => translate(locale as Locale, key), field.key)
}

/**
 * One complete field per declared type — label, control, help — generated
 * from the content type, never written by hand for a specific type.
 */
export function FieldControl({ field, value, onChange, disabled, supporting }: FieldControlProps) {
  const { t, locale } = useI18n()
  const controlType = resolveControlType(field)
  const label = fieldLabel(field, locale)
  const help = supporting ?? field.help?.[locale] ?? field.help?.it
  const text = typeof value === 'string' ? value : ''
  const counter = field.max && (controlType === 'string' || controlType === 'text') ? `${text.length} / ${field.max}` : undefined

  switch (controlType) {
    case 'string':
    case 'enum':
      return <TextField label={label} required={field.required} value={text} onChange={(event) => onChange(event.target.value)} disabled={disabled} supporting={help} counter={counter}/>
    case 'text':
      return <TextArea label={label} required={field.required} rows={4} value={text} onChange={(event) => onChange(event.target.value)} disabled={disabled} supporting={help} counter={counter}/>
    case 'richtext':
      // A body imported as Markdown stays a text box: the visual editor writes HTML and would silently change its format.
      return text.trim() && !looksLikeHtml(text)
        ? <TextArea label={label} required={field.required} rows={10} value={text} onChange={(event) => onChange(event.target.value)} disabled={disabled} supporting={help ?? t('fields.richtextHelp')}/>
        : <RichTextEditor label={label} required={field.required} value={text} onChange={onChange} disabled={disabled} supporting={help}/>
    case 'number':
      return <TextField label={label} required={field.required} type="number" min={field.min} max={field.max} value={typeof value === 'number' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))} disabled={disabled} supporting={help}/>
    case 'boolean':
      return <div className="field"><Switch label={label} supporting={help} checked={value === true} onChange={(event) => onChange(event.target.checked)} disabled={disabled}/></div>
    case 'date':
      return <TextField label={label} required={field.required} type="date" value={text.slice(0, 10)} onChange={(event) => onChange(event.target.value)} disabled={disabled} supporting={help}/>
    case 'media':
      return <MediaFieldControl label={label} required={field.required} value={value} onChange={onChange} disabled={disabled} supporting={help}/>
    case 'reference':
      return <TextField label={label} required={field.required} leadingIcon="link" value={text} onChange={(event) => onChange(event.target.value)} disabled={disabled} supporting={help ?? (field.to ? t('fields.referenceHelp', { to: field.to }) : undefined)}/>
    case 'list':
      if (!field.of?.length) return <ValueEditor name={field.key} label={label} value={Array.isArray(value) ? value : []} onChange={onChange} disabled={disabled}/>
      return <fieldset className="field-group">
        <legend className="field-label">{label}{field.required && <span className="field-required" aria-hidden="true"> *</span>}</legend>
        {help && <p className="field-support">{help}</p>}
        <ListFieldControl itemFields={field.of ?? []} value={Array.isArray(value) ? value : []} onChange={onChange} disabled={disabled}/>
      </fieldset>
    case 'json':
    default:
      // A group declared with its own fields: one small form per child. Anything else is shown by shape, never as JSON.
      if (field.of?.length) {
        const bag = isBag(value) ? value : {}
        return <GroupBox label={label} depth={1} defaultOpen>
          {help && <p className="field-support">{help}</p>}
          <div className="form-stack">
            {field.of.map((child) => <FieldControl key={child.key} field={child} value={bag[child.key]} disabled={disabled || child.protected === true}
              onChange={(next) => onChange({ ...bag, [child.key]: next })}/>)}
          </div>
        </GroupBox>
      }
      return <ValueEditor name={field.key} label={label} value={value ?? ''} onChange={onChange} disabled={disabled}/>
  }
}
